import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { slugify } from '@/features/products';

import type { BlockFormInput } from '../../schemas/block-form-schema';

export interface BlockDetailsFieldsProps {
  mode: 'create' | 'edit';
}

/** Name and key. The key follows the name (slugified) until it is edited by hand. */
export function BlockDetailsFields({ mode }: BlockDetailsFieldsProps) {
  const { t } = useTranslation('content');
  const form = useFormContext<BlockFormInput>();
  const [keyTouched, setKeyTouched] = useState(mode === 'edit');

  return (
    <FormSection title={t('sections.details')} description={t('sections.blockDetailsDescription')}>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.name')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus={mode === 'create'}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!keyTouched)
                      form.setValue('key', slugify(e.target.value), { shouldDirty: true });
                  }}
                />
              </FormControl>
              <FormDescription>{t('fields.nameHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.key')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="font-mono text-[13px]"
                  onChange={(e) => {
                    setKeyTouched(true);
                    field.onChange(e);
                  }}
                />
              </FormControl>
              <FormDescription>{t('fields.keyHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormSection>
  );
}
